import { useEffect } from 'react';

interface Props {
  user: any;
  onClose: () => void;
}

function formatWhatsApp(phone: string): string {
  const digits = phone.replace(/[\s\-]/g, '');
  return digits.startsWith('0') ? '972' + digits.slice(1) : digits;
}

export default function UserDrawer({ user, onClose }: Props) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const waLink = `https://wa.me/${formatWhatsApp(user.phone)}?text=${encodeURIComponent('שלום, ראיתי שנרשמת לפעילות שלנו 😊')}`;

  return (
    <>
      <div className="fixed inset-0 bg-black/20 z-40 transition-opacity" onClick={onClose} />
      <div className="fixed top-0 left-0 h-full w-full max-w-md bg-white border-r-2 border-rose-100 z-50 overflow-y-auto" dir="rtl" style={{ fontFamily: "'Heebo', sans-serif" }}>
        <div className="sticky top-0 bg-white border-b-2 border-rose-100 px-5 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-rose-100 text-rose-500 font-bold text-base flex-shrink-0">{user.name.charAt(0)}</div>
            <p className="font-bold text-gray-800 text-sm">{user.name}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl border-2 border-gray-200 text-gray-400 hover:bg-gray-50 cursor-pointer transition-all"><i className="ri-close-line text-base"></i></button>
        </div>
        <div className="p-5 space-y-5">
          <div className="flex gap-2">
            <a href={waLink} target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 border-emerald-200 bg-emerald-50 text-emerald-600 text-sm font-bold hover:bg-emerald-100 transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-whatsapp-line text-base"></i> שלח וואטסאפ
            </a>
            <a href={`tel:${user.phone}`} className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 border-rose-200 bg-rose-50 text-rose-500 text-sm font-bold hover:bg-rose-100 transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-phone-line text-base"></i> התקשר
            </a>
          </div>
          <div className="bg-white rounded-2xl border-2 border-rose-100 p-4">
            <p className="text-xs font-bold text-gray-500 mb-3 flex items-center gap-2"><i className="ri-user-line text-rose-400"></i> פרטים אישיים</p>
            <div className="space-y-2.5">
              <Row icon="ri-user-3-line" label="שם" value={user.name} />
              <Row icon="ri-phone-line" label="טלפון" value={user.phone} />
              {user.email && <Row icon="ri-mail-line" label="אימייל" value={user.email} />}
              {user.city && <Row icon="ri-map-pin-line" label="עיר" value={user.city} />}
            </div>
          </div>
          <div className="bg-white rounded-2xl border-2 border-rose-100 p-4">
            <p className="text-xs font-bold text-gray-500 mb-3 flex items-center gap-2"><i className="ri-calendar-check-line text-rose-400"></i> פרטי הרשמה</p>
            <div className="space-y-2.5">
              <div className="flex items-center justify-between"><span className="text-xs text-gray-400">פעילות / משרה</span><span className="text-xs font-semibold text-gray-700 text-right max-w-[60%]">{user.registerable?.title || user.registerable?.company || ''}</span></div>
              <Row icon="ri-calendar-line" label="תאריך הרשמה" value={user.registration_date} />
            </div>
          </div>
          <div className="bg-white rounded-2xl border-2 border-rose-100 p-4">
            <p className="text-xs font-bold text-gray-500 mb-3 flex items-center gap-2"><i className="ri-questionnaire-line text-rose-400"></i> שאלון הרשמה</p>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 rounded-xl bg-rose-50/50 border-2 border-rose-50">
                <span className="text-xs text-gray-600 font-medium">האם מוכר ברווחה?</span>
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full border-2 ${user.known_in_welfare ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>{user.known_in_welfare ? 'כן' : 'לא'}</span>
              </div>
              <div className="flex items-center justify-between p-3 rounded-xl bg-rose-50/50 border-2 border-rose-50">
                <span className="text-xs text-gray-600 font-medium">טווח שכר</span>
                <span className="text-xs font-bold px-2.5 py-1 rounded-full border-2 bg-violet-50 text-violet-600 border-violet-200">₪{user.salary_range}</span>
              </div>
              <div className="flex items-center justify-between p-3 rounded-xl bg-rose-50/50 border-2 border-rose-50">
                <span className="text-xs text-gray-600 font-medium">אינו עובד מעל שנה?</span>
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full border-2 ${user.unemployed_over_year ? 'bg-amber-50 text-amber-600 border-amber-200' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>{user.unemployed_over_year ? 'כן' : 'לא'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function Row({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-1.5 text-gray-400">
        <div className="w-4 h-4 flex items-center justify-center"><i className={`${icon} text-xs`}></i></div>
        <span className="text-xs">{label}</span>
      </div>
      <span className="text-xs font-semibold text-gray-700">{value}</span>
    </div>
  );
}
